import type { CellIndex, GameState, Mark } from '@/game/types';
import { cn } from '@/shared/cn';

interface BoardProps {
  state: GameState;
  canPlay: boolean;
  onPlay: (cell: CellIndex) => void;
}

function MarkGlyph({ mark }: { mark: Mark }) {
  if (mark === 'X') {
    return (
      <span className="mark-pop font-display text-5xl font-extrabold text-vz-x sm:text-6xl" aria-hidden>
        ×
      </span>
    );
  }
  return (
    <span
      className="mark-pop inline-flex h-12 w-12 items-center justify-center rounded-full border-[5px] border-vz-o sm:h-14 sm:w-14"
      aria-hidden
    />
  );
}

export function GameBoard({ state, canPlay, onPlay }: BoardProps) {
  const winning = new Set(state.winningLine ?? []);

  return (
    <div
      className="mx-auto grid max-w-sm grid-cols-3 gap-2 rounded-3xl bg-vz-surface/80 p-3 shadow-[0_20px_50px_-28px_rgba(15,118,110,0.55)] backdrop-blur sm:gap-3 sm:p-4"
      role="grid"
      aria-label="tic-tac-toe"
    >
      {state.board.map((cell, index) => {
        const cellIndex = index as CellIndex;
        const disabled = !canPlay || cell !== null || state.status !== 'playing';
        return (
          <button
            key={cellIndex}
            type="button"
            role="gridcell"
            disabled={disabled}
            aria-label={`cell ${cellIndex + 1}${cell ? ` ${cell}` : ''}`}
            className={cn(
              'flex aspect-square items-center justify-center rounded-2xl border-2 border-vz-line bg-vz-bg/60 transition',
              !disabled && 'hover:scale-[1.03] hover:border-vz-accent active:scale-95',
              disabled && 'cursor-default',
              winning.has(cellIndex) && 'win-cell border-vz-win bg-vz-win/20',
            )}
            onClick={() => onPlay(cellIndex)}
          >
            {cell ? <MarkGlyph mark={cell} /> : null}
          </button>
        );
      })}
    </div>
  );
}
